import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageInput from "@/components/ImageInput";
import { useCategories } from "@/lib/categories";
import { CoachPlayersAPI, PlayersAPI } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Save, Trash2 } from "lucide-react";
import { getSport } from "@/lib/sports";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-overline !text-zinc-400">{label}</Label>
    {children}
  </div>
);

const buildEmpty = (clubs) => ({
  name: "",
  position: "",
  sport: "football",
  category: "",
  club_name: clubs?.[0] || "",
  club_logo_url: "",
  season: "",
  cover_image_url: "",
  hero_image_url: "",
  primary_color: "#0066FF",
  jersey_number: "",
  date_of_birth: "",
  height_cm: "",
  weight_kg: "",
  bio: "",
  stats: {},
});

/**
 * Inline coach form: create new player OR edit an existing one.
 * - Scoped to coach's assigned_clubs.
 * - On save, returns the saved player (so parent can show QR).
 */
export default function CoachPlayerForm({
  initial = null,
  clubs = [],
  onCancel,
  onSaved,
  onDeleted,
}) {
  const isEdit = !!initial?.id;
  const categories = useCategories();
  const [form, setForm] = useState(() => {
    if (!initial) return buildEmpty(clubs);
    return {
      ...buildEmpty(clubs),
      ...initial,
      jersey_number: initial.jersey_number ?? "",
      height_cm: initial.height_cm ?? "",
      weight_kg: initial.weight_kg ?? "",
      stats: initial.stats || {},
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) =>
    setForm((f) => ({
      ...f,
      stats: {
        ...(f.stats || {}),
        [k]: v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0),
      },
    }));

  const sportDef = getSport(form.sport || "football");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Nom du joueur requis");
      return;
    }
    if (!form.club_name?.trim() || !clubs.includes(form.club_name)) {
      toast.error("Choisis un club parmi tes clubs assignés");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      };
      const result = isEdit
        ? await PlayersAPI.update(initial.id, payload)
        : await CoachPlayersAPI.create(payload);
      toast.success(
        isEdit ? "Joueur mis à jour" : "Joueur créé — QR généré",
      );
      onSaved?.(result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Supprimer définitivement ${initial.name} ?`)) return;
    try {
      await CoachPlayersAPI.remove(initial.id);
      toast.success("Joueur supprimé");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la suppression");
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="metal-card rounded-xl p-5 sm:p-7"
      data-testid="coach-player-form"
    >
      <button
        type="button"
        onClick={onCancel}
        data-testid="coach-player-form-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white mb-5"
      >
        <ChevronLeft className="w-3 h-3" /> Retour
      </button>

      <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mb-1">
        {isEdit ? "Modifier le joueur" : "Nouveau joueur"}
      </h2>
      <p className="text-zinc-400 text-sm font-body mb-6">
        {isEdit
          ? "Tu peux modifier la photo, les infos et les stats. La page publique se met à jour instantanément."
          : "Crée la fiche complète. Le QR code est généré automatiquement à l'enregistrement — il suffit de le graver sur la médaille."}
      </p>

      {/* Identité */}
      <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-3">
        Identité
      </h3>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Field label="Nom complet *">
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="cpf-name"
            className={inputClass}
            placeholder="Lucas Martin"
            required
          />
        </Field>
        <Field label="Position">
          <Input
            value={form.position}
            onChange={(e) => set("position", e.target.value)}
            data-testid="cpf-position"
            className={inputClass}
            placeholder="Attaquant, Milieu…"
          />
        </Field>
        <Field label="Club assigné *">
          <Select
            value={form.club_name}
            onValueChange={(v) => set("club_name", v)}
          >
            <SelectTrigger
              data-testid="cpf-club"
              className={inputClass}
            >
              <SelectValue placeholder="Choisis ton club" />
            </SelectTrigger>
            <SelectContent>
              {clubs.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Catégorie">
          <Input
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            data-testid="cpf-category"
            className={inputClass}
            placeholder="U17, Senior…"
            list="cpf-category-options"
          />
          <datalist id="cpf-category-options">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field label="Numéro de maillot">
          <Input
            type="number"
            value={form.jersey_number}
            onChange={(e) => set("jersey_number", e.target.value)}
            data-testid="cpf-jersey"
            className={inputClass}
            placeholder="10"
          />
        </Field>
        <Field label="Saison">
          <Input
            value={form.season}
            onChange={(e) => set("season", e.target.value)}
            className={inputClass}
            placeholder="2024/2025"
          />
        </Field>
        <Field label="Date de naissance">
          <Input
            type="date"
            value={form.date_of_birth || ""}
            onChange={(e) => set("date_of_birth", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Couleur accent">
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) => set("primary_color", e.target.value)}
              className="w-12 h-10 rounded border border-white/10 bg-transparent cursor-pointer"
            />
            <Input
              value={form.primary_color}
              onChange={(e) => set("primary_color", e.target.value)}
              className={inputClass}
            />
          </div>
        </Field>
      </div>

      {/* Photos */}
      <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-3">
        Photos
      </h3>
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Field label="Photo principale">
          <ImageInput
            value={form.hero_image_url}
            onChange={(v) => set("hero_image_url", v)}
            testId="cpf-hero"
            aspect="wide"
            placeholder="https://… ou galerie"
          />
        </Field>
        <Field label="Photo de couverture">
          <ImageInput
            value={form.cover_image_url}
            onChange={(v) => set("cover_image_url", v)}
            testId="cpf-cover"
            aspect="wide"
            placeholder="https://… ou galerie"
          />
        </Field>
      </div>

      {/* Stats */}
      {sportDef?.player_stats?.length > 0 && (
        <>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-3">
            Stats {sportDef.label}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
            {sportDef.player_stats.map((s) => (
              <Field key={s.key} label={s.label}>
                <Input
                  type="number"
                  placeholder="0"
                  value={form.stats?.[s.key] || ""}
                  onChange={(e) => setStat(s.key, e.target.value)}
                  data-testid={`cpf-stat-${s.key}`}
                  className={inputClass}
                />
              </Field>
            ))}
          </div>
        </>
      )}

      {/* Bio */}
      <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-zinc-300 mb-3">
        Présentation
      </h3>
      <Textarea
        value={form.bio || ""}
        onChange={(e) => set("bio", e.target.value)}
        rows={3}
        className={`${inputClass} mb-6`}
        placeholder="Quelques lignes sur le joueur…"
      />

      <div className="flex flex-wrap gap-3 pt-2 border-t border-white/10">
        <Button
          type="submit"
          disabled={saving}
          data-testid="cpf-submit"
          className="text-black font-bold uppercase tracking-[0.18em] text-xs h-11 px-7"
          style={{
            background:
              "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)",
          }}
        >
          <Save className="w-3.5 h-3.5 mr-2" />
          {saving
            ? "Enregistrement…"
            : isEdit
              ? "Mettre à jour"
              : "Créer le joueur"}
        </Button>
        {isEdit && (
          <Button
            type="button"
            onClick={onDelete}
            variant="outline"
            data-testid="cpf-delete"
            className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Supprimer
          </Button>
        )}
      </div>
    </motion.form>
  );
}
